import {useAui, useAuiState} from '@assistant-ui/react';
import {CalloutIcon} from '@wandb/components';
import React from 'react';

import {useTrackWBAgentComposer} from '../useTrackWBAgent';
import {type AgentSuggestion} from './catalog';
import {useActiveSuggestions} from './routeSuggestions';

/**
 * AgentSuggestionList renders the current route's suggestions as send-on-click
 * buttons in a 1- or 2-column grid. It always renders; the caller owns
 * visibility.
 */
export function AgentSuggestionList(): JSX.Element {
  const suggestions = useActiveSuggestions();

  return (
    <div className="grid grid-cols-1 [@container(min-width:699px)]:grid-cols-2 gap-8 self-stretch mt-12">
      {suggestions.map(suggestion => (
        <AgentSuggestionButton key={suggestion.title} suggestion={suggestion} />
      ))}
    </div>
  );
}

function AgentSuggestionButton({
  suggestion,
}: {
  suggestion: AgentSuggestion;
}): JSX.Element {
  const aui = useAui();
  const isDisabled = useAuiState(s => s.thread.isDisabled);
  const isRunning = useAuiState(s => s.thread.isRunning);
  const {trackSuggestionClicked, trackMessageSent} = useTrackWBAgentComposer();

  // Append the prompt as a user message, mirroring the composer's own
  // programmatic send (submitAnswers); the disabled button gates sends while
  // the thread is busy.
  const send = () => {
    trackSuggestionClicked(suggestion.title);
    trackMessageSent('suggestion');
    aui.thread().append({
      role: 'user',
      content: [{type: 'text', text: suggestion.prompt ?? suggestion.title}],
    });
  };

  return (
    <button
      type="button"
      disabled={isDisabled || isRunning}
      onClick={send}
      className="flex min-w-0 pr-16 pl-12 py-6 items-center gap-8 rounded-lg border border-moon-250 shadow-flat hover:bg-moon-100 disabled:cursor-not-allowed disabled:opacity-50">
      <CalloutIcon icon={suggestion.icon} color={suggestion.color} size="sm" />
      <span className="min-w-0 flex-1 text-left text-moon-800 truncate font-medium text-sm leading-[22.4px]">
        {suggestion.title}
      </span>
    </button>
  );
}
